import { logOfficeError } from "../../../src/telemetry";

/**
 * Open an EXISTING calendar item in whatever surface the host offers.
 *
 * Extracted so the two callers cannot drift: the "Open in Calendar" button on a
 * calendar card (``useOpenCalendarEvent``) and the CM-01 Graph fallback in
 * ``handlers.ts``, which has just created the event and now has to show it —
 * specification §6.2: "...creates the event via Graph ... then opens it in the
 * appointment form."
 *
 * Strategy (most → least native):
 *  1. displayAppointmentFormAsync  (Req Set 1.9+, classic Outlook)
 *  2. displayAppointmentForm       (Req Set 1.1+, classic Outlook, deprecated)
 *  3. openBrowserWindow webLink    (Req Set 1.2+, works in New Outlook / OWA)
 *  4. window.open                  (last resort, task-pane iframe)
 *
 * Graph REST ids are base64url; Office.js wants the older EWS base64 form, so
 * the id is converted first where the host can do it.
 *
 * Returns whether any rung of the ladder ran — false only on the dead end
 * where the host has no display API at all AND no webLink was supplied, so
 * callers can report honestly instead of claiming the event opened.
 */
/**
 * A Graph REST item id in the EWS form Office.js display APIs expect.
 *
 * Graph REST ids are base64**url**; ``displayAppointmentForm`` /
 * ``displayMessageForm`` want the older EWS base64. Handing them a REST id does
 * not throw — both APIs are fire-and-forget with no callback — so the window
 * simply never opens while the caller happily reports success. That is exactly
 * how the "Draft reply opens nothing, but the toast says it did" defect
 * presented (AIVA-7482 testing, 2026-08-19).
 *
 * Exported so the mail and calendar paths share one conversion. They were
 * independent before, and only the calendar one had it.
 *
 * Falls back to the raw id when the host has no converter or the id is already
 * EWS-shaped — the conversion is best-effort, never a hard failure.
 */
export function toEwsId(itemId: string): string {
  const mailbox = Office.context?.mailbox;
  try {
    if (itemId && typeof mailbox?.convertToEwsId === "function") {
      return mailbox.convertToEwsId(itemId, Office.MailboxEnums.RestVersion.v2_0);
    }
  } catch (err) {
    // Expected on hosts where the id is already EWS-shaped — the raw id is
    // used as-is. Logged (not surfaced) so a conversion failure that precedes
    // a failed open below is visible in telemetry rather than invisible.
    logOfficeError(err, "outlook-convert-ews-id");
  }
  return itemId;
}

export function openExistingAppointment(itemId: string, webLink: string): boolean {
  const mailbox = Office.context?.mailbox;

  const ewsId = toEwsId(itemId);

  if (ewsId && typeof mailbox?.displayAppointmentFormAsync === "function") {
    mailbox.displayAppointmentFormAsync(ewsId, () => {});
    return true;
  }

  if (ewsId && typeof mailbox?.displayAppointmentForm === "function") {
    mailbox.displayAppointmentForm(ewsId);
    return true;
  }

  if (!webLink) {
    // No display API on this host and nothing to open in a browser. Should not
    // happen (Graph always returns a webLink), but if it does the click would
    // otherwise die silently — record it so support can see why nothing opened.
    logOfficeError(
      new Error("openExistingAppointment: host has no display API and no webLink was provided"),
      "outlook-open-appointment-dead-end",
    );
    return false;
  }

  if (typeof Office.context?.ui?.openBrowserWindow === "function") {
    Office.context.ui.openBrowserWindow(webLink);
    return true;
  }

  window.open(webLink, "_blank");
  return true;
}
